import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import BankSelector from './BankSelector';
import './CardForm.css';

const CardForm = ({ card, onSubmit, onCancel, businessEntityId = null }) => {
  const t = useTranslation();
  const isMountedRef = useRef(true);
  
  // Track mounted state to prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  const [formData, setFormData] = useState({
    bankName: '',
    bankTier: null,
    cardSubType: '',
    creditLine: '',
    dateCreated: '',
    apr: '',
    annualFee: '',
    rewardsProgram: '',
    expirationDate: '',
    last4Digits: '',
    lastCreditIncreaseDate: '',
  });
  
  const [useCreatedDate, setUseCreatedDate] = useState(false);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (card) {
      // Convert Firestore timestamp to date string if needed
      const dateCreated = card.dateCreated?.toDate 
        ? card.dateCreated.toDate().toISOString().split('T')[0]
        : (card.dateCreated || '');
      
      const expirationDate = card.expirationDate?.toDate
        ? card.expirationDate.toDate().toISOString().split('T')[0]
        : (card.expirationDate || '');
      
      const lastCreditIncreaseDate = card.lastCreditIncreaseDate?.toDate
        ? card.lastCreditIncreaseDate.toDate().toISOString().split('T')[0]
        : (card.lastCreditIncreaseDate || '');

      setFormData({
        bankName: card.bankName || '',
        bankTier: card.bankTier || null,
        cardSubType: card.cardSubType || '',
        creditLine: card.creditLine || '',
        dateCreated: dateCreated,
        apr: card.apr || '',
        annualFee: card.annualFee || '',
        rewardsProgram: card.rewardsProgram || '',
        expirationDate: expirationDate,
        last4Digits: card.last4Digits || '',
        lastCreditIncreaseDate: lastCreditIncreaseDate,
      });
      
      // If no lastCreditIncreaseDate exists, suggest using created date
      if (!lastCreditIncreaseDate && dateCreated) {
        setUseCreatedDate(true);
        setFormData(prev => ({
          ...prev,
          lastCreditIncreaseDate: dateCreated,
        }));
      }
    }
  }, [card]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox' && name === 'useCreatedDate') {
      setUseCreatedDate(checked);
      if (checked && formData.dateCreated) {
        setFormData(prev => ({
          ...prev,
          lastCreditIncreaseDate: formData.dateCreated,
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };
  
  // Update lastCreditIncreaseDate when dateCreated changes and checkbox is checked
  useEffect(() => {
    if (useCreatedDate && formData.dateCreated) {
      setFormData(prev => ({
        ...prev,
        lastCreditIncreaseDate: formData.dateCreated,
      }));
    }
  }, [formData.dateCreated, useCreatedDate]);

  const handleBankChange = (bankData) => {
    setFormData(prev => ({
      ...prev,
      bankName: bankData.name,
      bankTier: bankData.tier,
    }));
    if (errors.bankName) {
      setErrors(prev => ({
        ...prev,
        bankName: '',
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.bankName.trim()) {
      newErrors.bankName = t('creditCardsInventory.bankNameRequired');
    }

    if (!formData.cardSubType.trim()) {
      newErrors.cardSubType = t('creditCardsInventory.cardSubTypeRequired');
    }

    if (!formData.creditLine) {
      newErrors.creditLine = t('creditCardsInventory.creditLineRequired');
    } else if (isNaN(parseFloat(formData.creditLine)) || parseFloat(formData.creditLine) < 0) {
      newErrors.creditLine = t('creditCardsInventory.creditLineInvalid');
    }

    if (!formData.dateCreated) {
      newErrors.dateCreated = t('creditCardsInventory.dateCreatedRequired');
    }
    
    if (!formData.lastCreditIncreaseDate) {
      newErrors.lastCreditIncreaseDate = t('creditCardsInventory.lastCreditIncreaseRequired');
    }

    // Optional field validations
    if (formData.apr && (isNaN(parseFloat(formData.apr)) || parseFloat(formData.apr) < 0 || parseFloat(formData.apr) > 100)) {
      newErrors.apr = t('creditCardsInventory.aprInvalid');
    }

    if (formData.annualFee && (isNaN(parseFloat(formData.annualFee)) || parseFloat(formData.annualFee) < 0)) {
      newErrors.annualFee = t('creditCardsInventory.annualFeeInvalid');
    }

    if (formData.last4Digits && (!/^\d{4}$/.test(formData.last4Digits))) {
      newErrors.last4Digits = t('creditCardsInventory.last4Invalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const submitData = {
        type: businessEntityId ? 'business' : 'personal',
        businessEntityId: businessEntityId || null,
        bankName: formData.bankName.trim(),
        bankTier: formData.bankTier,
        cardSubType: formData.cardSubType.trim(),
        creditLine: parseFloat(formData.creditLine),
        dateCreated: formData.dateCreated,
        apr: formData.apr ? parseFloat(formData.apr) : null,
        annualFee: formData.annualFee ? parseFloat(formData.annualFee) : null,
        rewardsProgram: formData.rewardsProgram.trim() || null,
        expirationDate: formData.expirationDate || null,
        last4Digits: formData.last4Digits.trim() || null,
        lastCreditIncreaseDate: formData.lastCreditIncreaseDate,
      };

      await onSubmit(submitData);
    } catch (error) {
      console.error('Error submitting form:', error);
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setErrors({ submit: t('creditCardsInventory.failedToSave') });
      }
    } finally {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <form className="card-form" onSubmit={handleSubmit}>
      <div className="card-form-header">
        <h3>{card ? t('creditCardsInventory.editCreditCard') : t('creditCardsInventory.addCreditCard')}</h3>
      </div>

      <div className="card-form-body">
        {errors.submit && (
          <div className="form-error-message">{errors.submit}</div>
        )}

        <div className="form-group">
          <label htmlFor="bankName">
            {t('creditCardsInventory.bankName')} <span className="required">*</span>
          </label>
          <BankSelector
            value={formData.bankName}
            onChange={handleBankChange}
            placeholder={t('creditCardsInventory.selectOrTypeBank')}
          />
          {errors.bankName && <span className="field-error">{errors.bankName}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="cardSubType">
            {t('creditCardsInventory.cardSubType')} <span className="required">*</span>
          </label>
          <input
            type="text"
            id="cardSubType"
            name="cardSubType"
            value={formData.cardSubType}
            onChange={handleChange}
            placeholder={t('creditCardsInventory.cardSubTypePlaceholder')}
            className={errors.cardSubType ? 'error' : ''}
          />
          {errors.cardSubType && <span className="field-error">{errors.cardSubType}</span>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="creditLine">
              {t('creditCardsInventory.creditLine')} <span className="required">*</span>
            </label>
            <input
              type="number"
              id="creditLine"
              name="creditLine"
              value={formData.creditLine}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              className={errors.creditLine ? 'error' : ''}
            />
            {errors.creditLine && <span className="field-error">{errors.creditLine}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="dateCreated">
              {t('creditCardsInventory.dateCreated')} <span className="required">*</span>
            </label>
            <input
              type="date"
              id="dateCreated"
              name="dateCreated"
              value={formData.dateCreated}
              onChange={handleChange}
              className={errors.dateCreated ? 'error' : ''}
            />
            {errors.dateCreated && <span className="field-error">{errors.dateCreated}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="apr">{t('creditCardsInventory.aprLabel')}</label>
            <input
              type="number"
              id="apr"
              name="apr"
              value={formData.apr}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              max="100"
              className={errors.apr ? 'error' : ''}
            />
            {errors.apr && <span className="field-error">{errors.apr}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="annualFee">{t('creditCardsInventory.annualFeeLabel')}</label>
            <input
              type="number"
              id="annualFee"
              name="annualFee"
              value={formData.annualFee}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              className={errors.annualFee ? 'error' : ''}
            />
            {errors.annualFee && <span className="field-error">{errors.annualFee}</span>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="rewardsProgram">{t('creditCardsInventory.rewardsProgram')}</label>
          <input
            type="text"
            id="rewardsProgram"
            name="rewardsProgram"
            value={formData.rewardsProgram}
            onChange={handleChange}
            placeholder={t('creditCardsInventory.rewardsPlaceholder')}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="expirationDate">{t('creditCardsInventory.expirationDate')}</label>
            <input
              type="date"
              id="expirationDate"
              name="expirationDate"
              value={formData.expirationDate}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="last4Digits">{t('creditCardsInventory.last4Digits')}</label>
            <input
              type="text"
              id="last4Digits"
              name="last4Digits"
              value={formData.last4Digits}
              onChange={handleChange}
              placeholder="1234"
              maxLength="4"
              pattern="\d{4}"
              className={errors.last4Digits ? 'error' : ''}
            />
            {errors.last4Digits && <span className="field-error">{errors.last4Digits}</span>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="lastCreditIncreaseDate">
            {t('creditCardsInventory.lastCreditIncreaseDate')} <span className="required">*</span>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <input
              type="date"
              id="lastCreditIncreaseDate"
              name="lastCreditIncreaseDate"
              value={formData.lastCreditIncreaseDate}
              onChange={handleChange}
              className={errors.lastCreditIncreaseDate ? 'error' : ''}
              //disabled={useCreatedDate}
            />
            {(!card || !formData.lastCreditIncreaseDate) && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  style={{ width: '25px' }}
                  type="checkbox"
                  name="useCreatedDate"
                  checked={useCreatedDate}
                  onChange={handleChange}
                />
                <span>{t('creditCardsInventory.useCreatedDate')}</span>
              </label>
            )}
          </div>
          {errors.lastCreditIncreaseDate && <span className="field-error">{errors.lastCreditIncreaseDate}</span>}
        </div>
      </div>

      <div className="card-form-footer">
        <button
          type="button"
          className="btn-cancel"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t('creditCardsInventory.cancel')}
        </button>
        <button
          type="submit"
          className="btn-submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? t('creditCardsInventory.saving') : (card ? t('creditCardsInventory.update') : t('creditCardsInventory.add'))} {t('creditCardsInventory.card')}
        </button>
      </div>
    </form>
  );
};

export default CardForm;

